import React, { useContext } from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { styled } from '@this/constants/themes';
import { Box, Button, Checkbox, FormControlLabel } from '@material-ui/core';
import { FormatListBulleted as FormatListBulletedIcon, Public as PublicIcon } from '@material-ui/icons';
import type { AdjustmentStatus } from '@this/domain/appoint/adjustment';
import type Adjustment from '@this/domain/appoint/adjustment';
import AppointContext from '@this/components/appoint/appoint_context';
import type Calendar from '@this/domain/appoint/calendar';
import type { AdjustmentFinishedType, AdjustmentsByStatusType } from '@this/components/appoint/common';
import { Left, ButtonProgress } from '@this/components/appoint/common';
import muiTheme from '@this/components/appoint/mui_theme';
import NewBigButton from '@this/components/appoint/shared/new_big_button/new_big_button';
import { TheBox } from '../dashboard';
import AdjustmentDrawer from './adjustment_drawer';
import OpenMeetingDrawer from './open_meeting_drawer';

interface Props {
  isLoading: boolean;
  openLeftColumn: boolean;
  openDrawer: boolean; // 調整一覧
  openOpenMeetingDrawer: boolean; // 公開URL
  selectedCalendars: Calendar[];
  adjustments: AdjustmentsByStatusType;
  selectedAdjustment: Adjustment | null;
  currentStatusTab: AdjustmentStatus;
  onCurrentStatusTabChange: (status: AdjustmentStatus) => void;
  adjustmentsFinished: AdjustmentFinishedType;
  isAdjustmentLoading: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  newAdjustmentButtonRef: React.MutableRefObject<null>;
  adjustmentsLoader: React.MutableRefObject<HTMLLIElement | null>;
  onDrawerOpen: (e: React.MouseEvent) => void; // 調整一覧
  onOpenMeetingDrawerOpen: () => void; // 公開URL
  onSelectedCalendarChange: (calendar: Calendar, checked: boolean) => void;
  onAdjustmentDrawerClose: () => void; // 調整一覧
  onOpenMeetingDrawerClose: () => void; // 公開URL
  onDetailOpen: (key?: string) => void;
}

const LeftMenu = observer(
  ({
    isLoading,
    openLeftColumn,
    openDrawer,
    openOpenMeetingDrawer,
    selectedCalendars,
    adjustments,
    selectedAdjustment,
    currentStatusTab,
    onCurrentStatusTabChange,
    isAdjustmentLoading,
    containerRef,
    newAdjustmentButtonRef,
    adjustmentsFinished,
    adjustmentsLoader,
    onDrawerOpen,
    onOpenMeetingDrawerOpen,
    onSelectedCalendarChange,
    onAdjustmentDrawerClose,
    onOpenMeetingDrawerClose,
    onDetailOpen
  }: Props) => {
    const contextValue = useContext(AppointContext);

    return (
      <TheLeft
        ref={containerRef}
        isClosed={contextValue.isMobile && !openLeftColumn && !openDrawer && !openOpenMeetingDrawer}
        isMobile={contextValue.isMobile}
      >
        <TheLeftHead>
          <NewBigButton to="/adjustments/new" ref={newAdjustmentButtonRef} label="新規作成" />
          <Box marginTop="14px">
            <MenuButton variant="text" onClick={e => onDrawerOpen(e)}>
              <MenuIcon>
                <FormatListBulletedIcon />
              </MenuIcon>
              <Box marginTop="-1px">調整一覧</Box>
            </MenuButton>
            <MenuButton variant="text" onClick={() => onOpenMeetingDrawerOpen()}>
              <MenuIcon>
                <PublicIcon />
              </MenuIcon>
              <Box marginTop="-1px">公開URL一覧</Box>
            </MenuButton>
          </Box>
        </TheLeftHead>
        <TheBox position="relative">
          {isLoading ? (
            <ButtonProgress />
          ) : (
            Object.values(
              _.groupBy(
                contextValue.calendars.filter(cc => cc.provider),
                c => c.editable
              )
            ).map((group, i) => (
              <div key={i} style={{ marginBottom: '10px' }}>
                <TheSubTitle>{i === 0 ? 'マイカレンダー' : '他のカレンダー'}</TheSubTitle>
                {group.map((c, j) => (
                  <Box
                    key={`${i}_${j}_${c.id}`}
                    style={{ marginLeft: '-5px', marginTop: '0px', overflow: 'hidden', whiteSpace: 'nowrap' }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={_.filter(selectedCalendars, sc => sc.id.indexOf(c.id) > -1).length > 0}
                          onChange={e => onSelectedCalendarChange(c, e.target.checked)}
                          size="small"
                          style={{
                            padding: '0px 3px 0px 2px',
                            color:
                              _.filter(selectedCalendars, sc => sc.id.indexOf(c.id) > -1).length > 0
                                ? c.backgroundColor || muiTheme.palette.secondary.main
                                : '#dddddd'
                          }}
                        />
                      }
                      label={
                        <span style={{ fontSize: '12px', letterSpacing: '0.05rem', color: '#606060' }}>
                          {c.name + (c.provider === 'outlook' && c.email ? ` (${c.email})` : '')}
                        </span>
                      }
                      style={{ margin: '0' }}
                    />
                  </Box>
                ))}
              </div>
            ))
          )}
        </TheBox>
        <AdjustmentDrawer
          open={openDrawer}
          onClose={onAdjustmentDrawerClose}
          adjustments={adjustments}
          selectedAdjustment={selectedAdjustment}
          currentStatusTab={currentStatusTab}
          adjustmentsFinished={adjustmentsFinished}
          onCurrentStatusTabChange={onCurrentStatusTabChange}
          containerRef={containerRef}
          onDetailOpen={onDetailOpen}
          adjustmentsLoader={adjustmentsLoader}
          isAdjustmentLoading={isAdjustmentLoading}
        />
        {openOpenMeetingDrawer && <OpenMeetingDrawer open onClose={onOpenMeetingDrawerClose} />}
      </TheLeft>
    );
  }
);

const TheLeft = styled(Left)<{ isClosed: boolean; isMobile: boolean }>`
  position: relative;
  width: 280px;
  box-sizing: border-box;
  margin: 0;
  padding-bottom: 24px;

  ${props =>
    props.isClosed &&
    `
    display: none;
  `}

  ${props =>
    props.isMobile &&
    `
    width: 0;
    overflow: hidden;
  `}
`;

const TheLeftHead = styled(Box)`
  padding: 20px 20px 0px;
`;

const MenuButton = styled(Button)`
  display: flex;
  font-size: 12px;
  font-weight: bold;
  padding: 6px 10px;
`;

const MenuIcon = styled.div`
  margin-right: 15px;
  height: 24px;
`;

const TheSubTitle = styled.div`
  padding-bottom: 5px;
  color: #606060;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 0.05rem;
  line-height: 20px;
`;

export default LeftMenu;
